// src/modules/operaciones/inventarios/InventariosMenu.js
//
// Menú principal del módulo "Inventario y Stock": muestra las tarjetas de los submódulos y renderiza
// el submódulo seleccionado (Ajustes, Negativos, Traslados, Ajustar Precio, Informes).
//
// • Al entrar al módulo, SIEMPRE se muestra el submenú (no se guarda la última vista en sessionStorage).
// • Los submódulos con `activo: false` no navegan: abren el modal "En construcción".
// • Al salir del módulo hacia el menú principal, simplemente se avisa al componente padre.

import React, { useState } from 'react'; // Importa React y el hook de estado
import Invent from './Invent'; // Submódulo: Ajustes
import InventariosNegativos from './InventariosNegativos'; // Submódulo: Negativos (¡NUEVO!)
import InventariosTraslados from './InventariosTraslados'; // Submódulo: Traslados pendientes
import InventariosPrecios from './InventariosPrecios'; // Submódulo: Ajustar Precios
import InventariosInformes from './InventariosInformes'; // Submódulo: Informes
import ModalConstruccion from './components/ModalConstruccion'; // Modal "En construcción"
import { subModulos } from './utils/menuComun'; // Definición de las tarjetas del menú

/* ============================ Componente principal ============================ */

export default function InventariosMenu({ usuario, onBackToMenu }) {
  // Inicializamos SIEMPRE en 'menu' para que no salte directo al último submódulo visitado
  const [vistaActiva, setVistaActiva] = useState('menu');
  const [moduloConstruccion, setModuloConstruccion] = useState(null);

  const volverAlMenuInterno = () => setVistaActiva('menu');

  const salirDeInventarios = () => {
    setVistaActiva('menu');
    if (onBackToMenu) onBackToMenu();
  };

  // Render condicional por vista
  if (vistaActiva === 'ajustes') return <Invent usuario={usuario} onBackToMenu={volverAlMenuInterno} />;
  if (vistaActiva === 'negativos') return <InventariosNegativos onBackToMenu={volverAlMenuInterno} />;
  if (vistaActiva === 'traslados') return <InventariosTraslados onBackToMenu={volverAlMenuInterno} />;
  if (vistaActiva === 'precios') return <InventariosPrecios onBackToMenu={volverAlMenuInterno} />;
  if (vistaActiva === 'informes') return <InventariosInformes onBackToMenu={volverAlMenuInterno} />;

  const handleModuloClick = (mod) => {
    if (mod.activo) {
      setVistaActiva(mod.id);
    } else {
      setModuloConstruccion(mod.titulo);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 relative">
      <div className="absolute top-6 left-6">
        <button
          onClick={salirDeInventarios}
          className="px-5 py-2.5 rounded-xl bg-white text-gray-700 text-sm font-bold shadow-sm border border-gray-200 hover:bg-gray-50 transition-all flex items-center gap-2 cursor-pointer"
        >
          ← Volver al Menú Principal
        </button>
      </div>

      <div className="max-w-6xl w-full">
        <h1 className="text-3xl font-extrabold text-gray-800 mb-2 text-center">Inventario y Stock</h1>
        <p className="text-gray-500 text-center mb-10">Selecciona una opción para comenzar</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subModulos.map((mod) => (
            <div
              key={mod.id}
              role="button"
              tabIndex={0}
              onClick={() => handleModuloClick(mod)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleModuloClick(mod); }
              }}
              className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                mod.activo ? 'hover:shadow-md hover:-translate-y-1 hover:border-blue-200' : 'opacity-75 hover:opacity-100 hover:shadow-sm'
              }`}
            >
              <div className="text-4xl mb-4">{mod.icon}</div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">{mod.titulo}</h3>
              <p className="text-sm text-gray-500 mb-4">{mod.desc}</p>

              {!mod.activo ? (
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                  En construcción
                </span>
              ) : (
                <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                  Disponible
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {moduloConstruccion && (
        <ModalConstruccion modulo={moduloConstruccion} onClose={() => setModuloConstruccion(null)} />
      )}
    </div>
  );
}